/**
 * Badge metadata for UI display.
 *
 * Copied from packages/frontend/src/config/badges.ts (also used there by the
 * user app's streak-badge reveal UI, so it cannot simply move) — mirrors the
 * BADGES registry in @charisol-spark/core/types/streak, but browser-safe (no
 * criteria functions, no AWS imports). Used here by AdminUserDetailPage to
 * render a user's earned badges.
 *
 * UI categories intentionally differ from backend:
 *   backend "milestone" → UI "achievement"
 *   backend "weekly" | "daily" → UI "streak"
 */

export type BadgeUiCategory = "achievement" | "streak";

export interface BadgeMeta {
  id: string;
  name: string;
  description: string;
  xpAward: number;
  category: BadgeUiCategory;
}

export const BADGE_META: readonly BadgeMeta[] = [
  // ── Achievements (backend: milestone) ──
  {
    id: "first_post",
    name: "First Spark",
    description: "Publish your first post",
    xpAward: 50,
    category: "achievement",
  },
  {
    id: "posts_10",
    name: "Getting Started",
    description: "Publish 10 posts",
    xpAward: 30,
    category: "achievement",
  },
  {
    id: "posts_50",
    name: "Prolific",
    description: "Publish 50 posts",
    xpAward: 100,
    category: "achievement",
  },
  {
    id: "posts_100",
    name: "Century Club",
    description: "Publish 100 posts",
    xpAward: 200,
    category: "achievement",
  },
  {
    id: "posts_500",
    name: "Content Machine",
    description: "Publish 500 posts",
    xpAward: 500,
    category: "achievement",
  },

  // ── Streaks (backend: weekly) ──
  {
    id: "weekly_2",
    name: "Two-Week Warrior",
    description: "Maintain a 2-week weekly posting streak",
    xpAward: 25,
    category: "streak",
  },
  {
    id: "weekly_4",
    name: "Monthly Momentum",
    description: "Maintain a 4-week weekly posting streak",
    xpAward: 50,
    category: "streak",
  },
  {
    id: "weekly_8",
    name: "Consistent Creator",
    description: "Maintain an 8-week weekly posting streak",
    xpAward: 100,
    category: "streak",
  },
  {
    id: "weekly_12",
    name: "Quarter Champion",
    description: "Maintain a 12-week weekly posting streak",
    xpAward: 150,
    category: "streak",
  },
  {
    id: "weekly_26",
    name: "Half-Year Hero",
    description: "Maintain a 26-week weekly posting streak",
    xpAward: 300,
    category: "streak",
  },
  {
    id: "weekly_52",
    name: "Year of Fire",
    description: "Maintain a 52-week weekly posting streak",
    xpAward: 500,
    category: "streak",
  },

  // ── Streaks (backend: daily) ──
  {
    id: "daily_3",
    name: "Hat Trick",
    description: "Maintain a 3-day daily posting streak",
    xpAward: 15,
    category: "streak",
  },
  {
    id: "daily_7",
    name: "Week Warrior",
    description: "Maintain a 7-day daily posting streak",
    xpAward: 50,
    category: "streak",
  },
  {
    id: "daily_14",
    name: "Fortnight Force",
    description: "Maintain a 14-day daily posting streak",
    xpAward: 100,
    category: "streak",
  },
  {
    id: "daily_30",
    name: "Monthly Machine",
    description: "Maintain a 30-day daily posting streak",
    xpAward: 200,
    category: "streak",
  },
  {
    id: "daily_60",
    name: "Relentless",
    description: "Maintain a 60-day daily posting streak",
    xpAward: 400,
    category: "streak",
  },
  {
    id: "daily_100",
    name: "Centurion",
    description: "Maintain a 100-day daily posting streak",
    xpAward: 750,
    category: "streak",
  },

  // ── Achievements (backend: milestone, event-triggered) ──
  {
    id: "night_mode",
    name: "Dark Side",
    description: "Switch to dark theme",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "light_mode",
    name: "Light Side",
    description: "Switch to light theme",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "double_tap",
    name: "Double Tap",
    description: "Publish 2+ posts in one day",
    xpAward: 25,
    category: "achievement",
  },
  {
    id: "night_owl",
    name: "Night Owl",
    description: "Publish a post after 11 PM",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "early_bird",
    name: "Early Bird",
    description: "Publish a post before 7 AM",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "first_schedule",
    name: "Time Traveler",
    description: "Schedule your first post",
    xpAward: 20,
    category: "achievement",
  },
  {
    id: "first_persona",
    name: "Alter Ego",
    description: "Create your second brand persona",
    xpAward: 20,
    category: "achievement",
  },
  {
    id: "persona_5",
    name: "Shapeshifter",
    description: "Create 5 brand personas",
    xpAward: 50,
    category: "achievement",
  },
  {
    id: "first_refine",
    name: "Polish",
    description: "Refine a draft with AI for the first time",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "first_export",
    name: "Takeaway",
    description: "Export a draft for the first time",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "first_upload",
    name: "Source Hunter",
    description: "Upload your first source content",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "completionist",
    name: "Completionist",
    description: "Get set up and publish your first post",
    xpAward: 30,
    category: "achievement",
  },
  {
    id: "first_freeze",
    name: "Ice Shield",
    description: "Use a streak freeze for the first time",
    xpAward: 15,
    category: "achievement",
  },
  {
    id: "profile_pic",
    name: "All Dressed Up",
    description: "Upload a profile picture",
    xpAward: 15,
    category: "achievement",
  },
] as const;

/** Lookup map: badge ID → full metadata */
export const BADGE_MAP = new Map<string, BadgeMeta>(
  BADGE_META.map((b) => [b.id, b])
);

/** Resolve a badge ID to its display name, falling back to the formatted ID. */
export function getBadgeName(id: string): string {
  return BADGE_MAP.get(id)?.name ?? id.replace(/_/g, " ");
}

/** Resolve a badge ID to its description. */
export function getBadgeDescription(id: string): string {
  return BADGE_MAP.get(id)?.description ?? "";
}

/** Resolve a badge ID to its UI category. */
export function getBadgeCategory(id: string): BadgeUiCategory {
  return BADGE_MAP.get(id)?.category ?? "achievement";
}
